import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Card, Form, Button, Alert, Spinner, Row, Col, Table } from 'react-bootstrap';
import { FaSearch, FaIdCard, FaArrowLeft } from 'react-icons/fa';
import consultaDniReniecApi from '../../../services/api/consultaDniReniecApi';

function formatDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (typeof d === 'object' && d.msg ? d.msg : JSON.stringify(d))).join(' ');
  }
  return String(detail);
}

const ConsultaDniReniec = () => {
  const navigate = useNavigate();
  const [dni, setDni] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const consultar = useCallback(async (e) => {
    e?.preventDefault?.();
    setError('');
    setData(null);
    const digits = dni.replace(/\D/g, '');
    if (digits.length !== 8) {
      setError('Ingrese un DNI de 8 dígitos.');
      return;
    }
    setLoading(true);
    try {
      const res = await consultaDniReniecApi.consultarDni(digits);
      if (res?.success && res?.data) {
        setData(res.data);
      } else {
        setError(res?.message || 'Respuesta inesperada del servidor.');
      }
    } catch (err) {
      const d = err.response?.data?.detail ?? err.response?.data?.message ?? err.message;
      setError(formatDetail(d) || 'No se pudo completar la consulta.');
    } finally {
      setLoading(false);
    }
  }, [dni]);

  const dom = data?.domiciliado;

  return (
    <>
      <style>
        {`
          .dh-maintenance-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          .dh-main-title { font-size: 2rem; font-weight: 700; margin: 0; }
          .dh-main-subtitle { font-size: 1rem; opacity: 0.9; margin: 0.5rem 0 0 0; }
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.25rem 1.75rem;
            border: none;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            font-weight: 700;
          }
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
          }
          .btn-dh-primary:hover { color: white; filter: brightness(1.06); }
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
          }
          .btn-dh-secondary:hover { color: white; }
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
            transition: all 0.3s ease;
          }
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
          }
          .dh-enhanced-table {
            border-collapse: separate;
            border-spacing: 0;
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.05);
            background: white;
          }
          .dh-enhanced-table thead th {
            background: #374151 !important;
            color: white !important;
            font-weight: 700 !important;
            font-size: 0.85rem !important;
            padding: 0.75rem !important;
            border: none !important;
          }
          .dh-enhanced-table tbody td,
          .dh-enhanced-table tbody th {
            padding: 0.65rem 0.75rem;
            border-bottom: 1px solid #f3f4f6;
            font-size: 0.9rem;
            vertical-align: middle;
          }
        `}
      </style>

      <div className="dh-maintenance-bg">
        <div className="dh-main-header">
          <Container>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center gap-3">
                <FaIdCard size={36} />
                <div>
                  <h1 className="dh-main-title">Consulta DNI — RENIEC</h1>
                  <p className="dh-main-subtitle">
                    Datos vía servicio del servidor (miapi.cloud). Debe estar autenticado en SGC.
                  </p>
                </div>
              </div>
              <Button className="btn-dh-secondary d-flex align-items-center" onClick={() => navigate('/home')}>
                <FaArrowLeft className="me-2" />
                Volver
              </Button>
            </div>
          </Container>
        </div>

        <Container fluid className="pb-5">
          <Row className="justify-content-center">
            <Col xs={12} lg={8} xl={7}>
              <Card className="dh-card">
                <Card.Header className="dh-card-header d-flex align-items-center gap-2">
                  <FaSearch />
                  Consulta
                </Card.Header>
                <Card.Body className="p-4">
                  <Form onSubmit={consultar}>
                    <Row className="g-3 align-items-end">
                      <Col xs={12} md={7}>
                        <Form.Group>
                          <Form.Label className="fw-semibold text-secondary">Número de DNI</Form.Label>
                          <Form.Control
                            className="dh-form-control"
                            type="text"
                            inputMode="numeric"
                            maxLength={12}
                            placeholder="Ej: 46087162"
                            value={dni}
                            onChange={(ev) => setDni(ev.target.value)}
                            disabled={loading}
                            autoComplete="off"
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={5}>
                        <Button type="submit" className="btn-dh-primary w-100 d-flex align-items-center justify-content-center gap-2" disabled={loading}>
                          {loading ? (
                            <>
                              <Spinner animation="border" size="sm" />
                              Consultando…
                            </>
                          ) : (
                            <>
                              <FaSearch />
                              Consultar
                            </>
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Form>

                  {error && (
                    <Alert variant="danger" className="mt-3 mb-0">
                      {error}
                    </Alert>
                  )}

                  {data && (
                    <div className="mt-4">
                      <h6 className="text-secondary mb-3 fw-semibold">Resultado</h6>
                      <Table className="dh-enhanced-table mb-0" size="sm">
                        <tbody>
                          <tr>
                            <th className="w-25 bg-light">DNI</th>
                            <td>{data.numero_documento}</td>
                          </tr>
                          <tr>
                            <th className="bg-light">Nombre completo</th>
                            <td className="fw-semibold">{data.nombre_completo || '—'}</td>
                          </tr>
                          <tr>
                            <th className="bg-light">Nombres</th>
                            <td>{data.nombres || '—'}</td>
                          </tr>
                          <tr>
                            <th className="bg-light">Apellido paterno</th>
                            <td>{data.apellido_paterno || '—'}</td>
                          </tr>
                          <tr>
                            <th className="bg-light">Apellido materno</th>
                            <td>{data.apellido_materno || '—'}</td>
                          </tr>
                          {data.proveedor && (
                            <tr>
                              <th className="bg-light">Proveedor</th>
                              <td className="small text-muted">{data.proveedor}</td>
                            </tr>
                          )}
                        </tbody>
                      </Table>

                      {dom && typeof dom === 'object' && (
                        <>
                          <h6 className="text-secondary mt-4 mb-2 fw-semibold">Domicilio</h6>
                          <Table className="dh-enhanced-table mb-0" size="sm">
                            <tbody>
                              {Object.entries(dom).map(([k, v]) => (
                                <tr key={k}>
                                  <th className="w-25 bg-light text-capitalize" style={{ whiteSpace: 'nowrap' }}>
                                    {k.replace(/_/g, ' ')}
                                  </th>
                                  <td>{v != null ? String(v) : '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </>
                      )}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default ConsultaDniReniec;
